'use client';

/**
 * Navigation Provider
 * Provides centralized navigation state for the entire application
 *
 * This provider:
 * - Fetches navigation structure from /api/navigation
 * - Caches navigation data in localStorage with TTL (5 minutes)
 * - Syncs user preferences to Supabase (expanded groups, simplified view)
 * - Manages expanded group state
 * - Handles simplified view toggle
 * - Invalidates on user/organization change
 * - Tracks active path for highlighting
 *
 * Storage Architecture:
 * - Navigation cache: localStorage only (TTL-based, ephemeral)
 * - User preferences: Supabase sync with localStorage cache (persistent, cross-device)
 */

import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { usePathname } from 'next/navigation';
import logger from '@/lib/logger';
import type {
  NavigationContextValue,
  NavigationStructure,
  NavigationCache,
  NavigationPreferences,
  ResolvedNavItem,
  ResolvedNavGroup,
  DbNavigationGroup,
  DbNavigationItem,
  NavPosition,
  NavigationApiResponse,
} from './types';
import { resolveIcon } from './icons';
import { markActiveNavItems } from './utils';
import { useUserPreferencesObject } from '@/hooks/syncHooks';
import { useLicensing } from '@/lib/licensing/hooks';
import { FEATURE_KEYS } from '@/lib/licensing/types';

// ============================================================================
// Constants
// ============================================================================

/** Cache TTL in milliseconds (5 minutes) */
const CACHE_TTL = 5 * 60 * 1000;

/** Cache format version - increment when format changes to invalidate old caches */
const CACHE_FORMAT_VERSION = 11; // v11: Navigation restructure (8 groups, primary_path for leaf mode)

/** LocalStorage key for cache (TTL-based, stays in localStorage) */
const CACHE_STORAGE_KEY = 'neutralaiz-navigation-cache';

/** Preference key for Supabase sync */
const PREFS_KEY = 'navigation_preferences';

/** Default preferences */
const DEFAULT_NAV_PREFERENCES: NavigationPreferences = {
  expandedGroups: [],
  simplifiedView: false,
  sidebarCollapsed: false,
};

function getNavigationCacheKey(userId: string | null, organizationId: string | null): string {
  return `${CACHE_STORAGE_KEY}:${userId || 'anonymous'}:${organizationId || 'none'}`;
}

function removeNavigationCache(userId?: string | null, organizationId?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CACHE_STORAGE_KEY);
    if (userId !== undefined || organizationId !== undefined) {
      localStorage.removeItem(getNavigationCacheKey(userId || null, organizationId || null));
      return;
    }
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${CACHE_STORAGE_KEY}:`)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable
  }
}

// ============================================================================
// Context
// ============================================================================

export const NavigationContext = createContext<NavigationContextValue | null>(null);

// ============================================================================
// Types
// ============================================================================

interface NavigationProviderProps {
  children: ReactNode;

  /** Current user ID for cache invalidation */
  userId?: string | null;

  /** Current organization ID for cache invalidation */
  organizationId?: string | null;

  /** API endpoint for fetching navigation */
  apiEndpoint?: string;

  /** Initial navigation data (from server) */
  initialNavigation?: NavigationStructure | null;

  /** Feature flags for filtering */
  featureFlags?: Record<string, boolean>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get cached navigation from localStorage (cache only, not user preferences)
 */
function getCachedNavigation(
  userId: string | null,
  organizationId: string | null
): NavigationStructure | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = localStorage.getItem(getNavigationCacheKey(userId, organizationId));
    if (!cached) return null;

    const parsed: NavigationCache = JSON.parse(cached);

    // Check if cache is valid
    const now = Date.now();
    if (now - parsed.timestamp > CACHE_TTL) {
      removeNavigationCache(userId, organizationId);
      return null;
    }

    // Check if user/org matches
    if (parsed.userId !== userId || parsed.organizationId !== organizationId) {
      removeNavigationCache(userId, organizationId);
      return null;
    }

    // Check cache format version (invalidate old format caches)
    if ((parsed as { formatVersion?: number }).formatVersion !== CACHE_FORMAT_VERSION) {
      logger.debug('NavigationProvider', 'Cache format outdated, refetching');
      removeNavigationCache(userId, organizationId);
      return null;
    }

    return parsed.navigation;
  } catch {
    removeNavigationCache(userId, organizationId);
    return null;
  }
}

/**
 * Save navigation to localStorage cache
 */
function setCachedNavigation(
  navigation: NavigationStructure,
  userId: string | null,
  organizationId: string | null
): void {
  if (typeof window === 'undefined') return;

  try {
    const cache: NavigationCache & { formatVersion: number } = {
      navigation,
      timestamp: Date.now(),
      userId,
      organizationId,
      version: navigation.cacheVersion,
      formatVersion: CACHE_FORMAT_VERSION, // Include format version for cache invalidation
    };
    localStorage.setItem(getNavigationCacheKey(userId, organizationId), JSON.stringify(cache));
  } catch {
    // localStorage full or unavailable
    logger.warn('NavigationProvider', 'Failed to cache navigation data');
  }
}

// User preferences are now synced via useUserPreferencesObject (Supabase + localStorage cache)

/**
 * Check if a path matches a pattern (supports [param] and [...catchAll])
 */
function pathMatches(path: string, pattern: string | null | undefined): boolean {
  if (!pattern) return false;

  // Convert pattern to regex
  const regexPattern = pattern
    .replace(/\[\.\.\.[\w]+\]/g, '.+') // [...catchAll] -> .+
    .replace(/\[[\w]+\]/g, '[^/]+'); // [param] -> [^/]+

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(path);
}

/**
 * Determine if an item is active based on current path
 * @deprecated Use markActiveNavItems from ./utils instead for "Most Specific Match Wins" algorithm
 */
function isItemActiveLegacy(item: ResolvedNavItem, currentPath: string): boolean {
  // Exact match
  if (item.path === currentPath) return true;

  // Pattern match (for dynamic routes)
  if (item.pathPattern && pathMatches(currentPath, item.pathPattern)) return true;

  // Prefix match for nested routes (e.g., /settings matches /settings/profile)
  if (currentPath.startsWith(item.path + '/')) return true;

  return false;
}

/**
 * Transform database items to resolved items.
 *
 * The `icon ?? icon_name` fallback that used to live here existed because the
 * DbNavigationItem type claimed a column (`icon_name`) that the table has never
 * had. The type now matches public.navigation_items, so the real column is read
 * directly and the cast is gone.
 *
 * pathPattern / activeMatchMode / target / keyboardShortcut are NOT set: no such
 * columns exist, so those reads were always undefined. They remain optional on
 * ResolvedNavItem and lib/navigation/utils.ts already falls back to
 * getDefaultMatchMode() when activeMatchMode/pathPattern are absent, so dropping
 * the dead reads changes no behaviour.
 */
function transformItem(
  dbItem: DbNavigationItem,
  currentPath: string,
  isOverridden = false
): ResolvedNavItem {
  return {
    key: dbItem.key,
    parentKey: dbItem.parent_key,
    groupKey: dbItem.group_key,
    label: dbItem.label,
    shortLabel: dbItem.short_label ?? undefined,
    description: dbItem.description ?? undefined,
    path: dbItem.path,
    external: dbItem.external,
    icon: resolveIcon(dbItem.icon),
    iconName: dbItem.icon,
    colorScheme: dbItem.color_scheme ?? 'slate',
    positions: dbItem.positions,
    sortOrder: dbItem.sort_order,
    badge: dbItem.badge_type
      ? {
          type: dbItem.badge_type,
          value: dbItem.badge_value ?? '',
          color: dbItem.badge_color ?? undefined,
        }
      : undefined,
    hidden: !dbItem.visible,
    isOverridden,
    isActive: false, // Will be calculated after
    submenuContext: dbItem.submenu_context ?? undefined,
    submenuCollapsible: dbItem.submenu_collapsible ?? undefined,
    submenuDefaultCollapsed: dbItem.submenu_default_collapsed ?? undefined,
    authRequirement: dbItem.auth_requirement ?? undefined,
    requiredRoles: dbItem.required_roles ?? null,
  };
}

/**
 * Transform database group to resolved group.
 *
 * `collapsedByDefault` is the negation of the real `default_expanded` column —
 * there has never been a `collapsed_by_default` column, so the old
 * `collapsed_by_default ?? default_expanded === false` expression was really
 * just the fallback half doing all the work.
 *
 * navigation_groups has no `description` column, so no description is resolved.
 */
function transformGroup(dbGroup: DbNavigationGroup): ResolvedNavGroup {
  return {
    key: dbGroup.key,
    label: dbGroup.label,
    icon: resolveIcon(dbGroup.icon),
    iconName: dbGroup.icon,
    colorScheme: dbGroup.color_scheme,
    positions: dbGroup.positions,
    sortOrder: dbGroup.sort_order,
    collapsedByDefault: dbGroup.default_expanded === false,
    visible: dbGroup.visible,
    primaryPath: dbGroup.primary_path ?? undefined,
    authRequirement: dbGroup.auth_requirement ?? undefined,
    requiredRoles: dbGroup.required_roles ?? null,
    items: [],
  };
}

/**
 * Build navigation structure from API response
 */
/**
 * Map navigation item paths to Monaiq feature keys.
 * Items whose path prefix matches an unlicensed feature will be hidden.
 */
const NAV_PATH_FEATURE_MAP: Array<[string, string]> = [
  ['/training', FEATURE_KEYS.TRAINING_MODE],
  ['/knowledge-base', FEATURE_KEYS.KNOWLEDGE_BASE],
];

/**
 * Filter navigation items by license entitlements.
 * Items matching unlicensed features are marked as hidden.
 */
function applyLicenseFiltering(
  items: DbNavigationItem[],
  isFeatureAllowed: (key: string) => boolean,
): DbNavigationItem[] {
  return items.map(item => {
    for (const [pathPrefix, featureKey] of NAV_PATH_FEATURE_MAP) {
      if (item.path?.startsWith(pathPrefix) && !isFeatureAllowed(featureKey)) {
        return { ...item, visible: false };
      }
    }
    return item;
  });
}

function buildNavigationStructure(
  groups: DbNavigationGroup[],
  items: DbNavigationItem[],
  currentPath: string
): NavigationStructure {
  // Transform groups
  const resolvedGroups = groups
    .filter((g) => g.visible)
    .map(transformGroup)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Transform and organize items
  const allItems: ResolvedNavItem[] = [];
  const itemsByGroup = new Map<string, ResolvedNavItem[]>();
  const itemsByKey = new Map<string, ResolvedNavItem>();

  // First pass: transform all items (don't set isActive yet - will be done centrally)
  for (const dbItem of items) {
    const item = transformItem(dbItem, currentPath);
    allItems.push(item);
    itemsByKey.set(item.key, item);

    if (!item.hidden) {
      const groupItems = itemsByGroup.get(item.groupKey) ?? [];
      groupItems.push(item);
      itemsByGroup.set(item.groupKey, groupItems);
    }
  }

  // Second pass: build parent-child relationships
  for (const item of allItems) {
    if (item.parentKey) {
      const parent = itemsByKey.get(item.parentKey);
      if (parent) {
        parent.children = parent.children ?? [];
        parent.children.push(item);
        parent.children.sort((a, b) => a.sortOrder - b.sortOrder);
      }
    }
  }

  // Third pass: Mark active state using "Most Specific Match Wins" algorithm
  // This ensures only ONE item is marked as active, even with overlapping paths
  markActiveNavItems(allItems, currentPath);

  // Assign items to groups
  for (const group of resolvedGroups) {
    const groupItems = itemsByGroup.get(group.key) ?? [];
    // Only include top-level items (no parent) in group
    group.items = groupItems
      .filter((item) => !item.parentKey)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // Build position-based views
  const filterByPosition = (
    position: NavPosition,
    source: ResolvedNavItem[]
  ): ResolvedNavItem[] =>
    source
      .filter((item) => !item.hidden && item.positions.includes(position))
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const filterGroupsByPosition = (position: NavPosition): ResolvedNavGroup[] =>
    resolvedGroups
      .filter((g) => g.visible && g.positions.includes(position))
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => item.positions.includes(position)),
      }))
      .filter((g) => g.items.length > 0);

  return {
    groups: resolvedGroups,
    allItems,
    byPosition: {
      main: filterByPosition('main', allItems),
      sidebar: filterGroupsByPosition('sidebar'),
      mobile: filterByPosition('mobile', allItems),
      userMenu: filterGroupsByPosition('user-menu'),
      footer: filterByPosition('footer', allItems),
    },
    updatedAt: new Date().toISOString(),
    cacheVersion: Date.now().toString(),
  };
}

// ============================================================================
// Provider Component
// ============================================================================

export function NavigationProvider({
  children,
  userId = null,
  organizationId = null,
  apiEndpoint = '/api/navigation',
  initialNavigation = null,
  featureFlags = {},
}: NavigationProviderProps) {
  const pathname = usePathname();
  const fetchInProgress = useRef(false);
  const { isFeatureAllowed } = useLicensing();

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  // IMPORTANT: Don't read from localStorage in initial state to avoid hydration mismatch
  // Server has no localStorage, so initial state must be consistent (null/loading)
  // The useEffect below will load from cache on client mount
  const [navigation, setNavigation] = useState<NavigationStructure | null>(
    initialNavigation ?? null
  );
  const [isLoading, setIsLoading] = useState(!initialNavigation);
  const [error, setError] = useState<Error | null>(null);
  const [hasMounted, setHasMounted] = useState(false);

  // User preferences with Supabase sync (local-first with background sync)
  const [navPrefs, setNavPrefs] = useUserPreferencesObject<NavigationPreferences>(
    PREFS_KEY,
    DEFAULT_NAV_PREFERENCES,
    { syncDebounceMs: 500 }
  );

  // Convert expandedGroups array to Set for efficient lookup
  const expandedGroups = useMemo(
    () => new Set(navPrefs.expandedGroups),
    [navPrefs.expandedGroups]
  );
  const simplifiedView = navPrefs.simplifiedView;

  // -------------------------------------------------------------------------
  // Update active state when path changes
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!navigation) return;

    // Deep clone items to avoid mutating state directly
    const cloneItem = (item: ResolvedNavItem): ResolvedNavItem => ({
      ...item,
      isActive: false, // Reset - will be set by markActiveNavItems
      children: item.children?.map(cloneItem),
    });

    const updatedAllItems = navigation.allItems.map(cloneItem);

    // Use "Most Specific Match Wins" algorithm
    // This ensures only ONE item is marked as active
    markActiveNavItems(updatedAllItems, pathname);

    // Create a lookup map for updated items
    const updatedItemsMap = new Map<string, ResolvedNavItem>();
    const buildMap = (items: ResolvedNavItem[]) => {
      for (const item of items) {
        updatedItemsMap.set(item.key, item);
        if (item.children) buildMap(item.children);
      }
    };
    buildMap(updatedAllItems);

    // Update items in groups using the lookup map
    const updatedGroups = navigation.groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        const updatedItem = updatedItemsMap.get(item.key);
        if (!updatedItem) return item;
        return {
          ...updatedItem,
          children: item.children?.map((child) =>
            updatedItemsMap.get(child.key) ?? child
          ),
        };
      }),
    }));

    // Rebuild byPosition
    const filterByPosition = (
      position: NavPosition,
      source: ResolvedNavItem[]
    ): ResolvedNavItem[] =>
      source
        .filter((item) => !item.hidden && item.positions.includes(position))
        .sort((a, b) => a.sortOrder - b.sortOrder);

    const filterGroupsByPosition = (position: NavPosition): ResolvedNavGroup[] =>
      updatedGroups
        .filter((g) => g.visible && g.positions.includes(position))
        .map((g) => ({
          ...g,
          items: g.items.filter((item) => item.positions.includes(position)),
        }))
        .filter((g) => g.items.length > 0);

    setNavigation({
      ...navigation,
      allItems: updatedAllItems,
      groups: updatedGroups,
      byPosition: {
        main: filterByPosition('main', updatedAllItems),
        sidebar: filterGroupsByPosition('sidebar'),
        mobile: filterByPosition('mobile', updatedAllItems),
        userMenu: filterGroupsByPosition('user-menu'),
        footer: filterByPosition('footer', updatedAllItems),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // -------------------------------------------------------------------------
  // Fetch Navigation
  // -------------------------------------------------------------------------

  const fetchNavigation = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      // Note: org_id requires org_type to be set as well per the API contract (route.ts lines 74-78)
      // For now, we don't send org_id without org_type to avoid 400 errors
      // TODO: Add org_type support when organization-specific navigation is needed

      const response = await fetch(
        `${apiEndpoint}${params.toString() ? `?${params}` : ''}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch navigation: ${response.status}`);
      }

      const data: NavigationApiResponse = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.error ?? 'Unknown error fetching navigation');
      }

      const { groups, items } = data.data;

      // Filter items by license entitlements (hide unlicensed features)
      const licensedItems = applyLicenseFiltering(items || [], isFeatureAllowed);

      // Build navigation structure from API data - NO FALLBACKS
      // If API returns empty arrays, navigation will be empty
      const navStructure = buildNavigationStructure(groups || [], licensedItems, pathname);

      setNavigation(navStructure);
      setCachedNavigation(navStructure, userId, organizationId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      logger.error('NavigationProvider', 'Failed to fetch navigation from API', { error: error.message });

      // NO FALLBACKS - set empty navigation on error
      const emptyNavStructure = buildNavigationStructure([], [], pathname);
      setNavigation(emptyNavStructure);
      setError(error);
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [apiEndpoint, organizationId, pathname, userId, isFeatureAllowed]);

  // -------------------------------------------------------------------------
  // Client-side hydration from cache (runs only after mount to avoid hydration mismatch)
  // -------------------------------------------------------------------------

  useEffect(() => {
    setHasMounted(true);

    // If we don't have initial navigation from server, try cache then fetch
    if (!navigation) {
      const cached = getCachedNavigation(userId, organizationId);
      if (cached) {
        setNavigation(cached);
        setIsLoading(false);
      } else {
        fetchNavigation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Invalidate on user/org change
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Clear cache and refetch when user or org changes
    removeNavigationCache(userId, organizationId);
    if (!fetchInProgress.current) {
      fetchNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, organizationId]);

  // -------------------------------------------------------------------------
  // Invalidate on masquerade change
  // -------------------------------------------------------------------------

  useEffect(() => {
    const handler = () => {
      removeNavigationCache(userId, organizationId);
      fetchNavigation();
    };
    window.addEventListener('masquerade-changed', handler);
    window.addEventListener('tier-simulator-changed', handler);
    return () => {
      window.removeEventListener('masquerade-changed', handler);
      window.removeEventListener('tier-simulator-changed', handler);
    };
  }, [fetchNavigation]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const refresh = useCallback(async () => {
    removeNavigationCache(userId, organizationId);
    await fetchNavigation();
  }, [fetchNavigation]);

  const toggleGroup = useCallback((groupKey: string) => {
    const currentSet = new Set(navPrefs.expandedGroups);
    if (currentSet.has(groupKey)) {
      // Closing — just remove
      currentSet.delete(groupKey);
    } else {
      // Opening — accordion: close all non-active groups, keep only this one + active groups
      const activeGroupKeys = new Set<string>();
      if (navigation) {
        for (const group of navigation.groups) {
          const hasActive = group.items.some(
            item => item.isActive || item.children?.some(child => child.isActive)
          );
          if (hasActive) activeGroupKeys.add(group.key);
        }
      }
      // Close non-active groups
      for (const key of currentSet) {
        if (!activeGroupKeys.has(key)) {
          currentSet.delete(key);
        }
      }
      currentSet.add(groupKey);
    }
    // Save via Supabase sync
    setNavPrefs({ expandedGroups: Array.from(currentSet) });
  }, [navPrefs.expandedGroups, setNavPrefs, navigation]);

  const setExpandedGroups = useCallback((groups: Set<string>) => {
    // Save via Supabase sync
    setNavPrefs({ expandedGroups: Array.from(groups) });
  }, [setNavPrefs]);

  const setSimplifiedView = useCallback((simplified: boolean) => {
    // Save via Supabase sync
    setNavPrefs({ simplifiedView: simplified });
  }, [setNavPrefs]);

  // -------------------------------------------------------------------------
  // Initialize expanded groups based on defaults
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (navigation && expandedGroups.size === 0) {
      // Expand groups that have active items or are not collapsed by default
      const initialExpanded = new Set<string>();

      for (const group of navigation.groups) {
        // Expand if not collapsed by default
        if (!group.collapsedByDefault) {
          initialExpanded.add(group.key);
        }

        // Expand if contains active item
        const hasActive = group.items.some(
          (item) =>
            item.isActive || item.children?.some((child) => child.isActive)
        );
        if (hasActive) {
          initialExpanded.add(group.key);
        }
      }

      if (initialExpanded.size > 0) {
        setExpandedGroups(initialExpanded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation?.groups]);

  // -------------------------------------------------------------------------
  // Context Value
  // -------------------------------------------------------------------------

  const value: NavigationContextValue = useMemo(
    () => ({
      navigation,
      isLoading,
      error,
      refresh,
      expandedGroups,
      toggleGroup,
      setExpandedGroups,
      simplifiedView,
      setSimplifiedView,
      activePath: pathname,
    }),
    [
      navigation,
      isLoading,
      error,
      refresh,
      expandedGroups,
      toggleGroup,
      setExpandedGroups,
      simplifiedView,
      setSimplifiedView,
      pathname,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

// ============================================================================
// Export
// ============================================================================

export default NavigationProvider;
